import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckIcon, LoaderIcon, LockIcon } from 'lucide-react';
import { useCart } from '../contexts/CartContext';
import { useCheckoutForm, type CheckoutFields, type PaymentMethod } from '../hooks/useCheckoutForm';
import { shippingCost, shippingOptions, FREE_SHIPPING_THRESHOLD } from '../utils/shipping';
import { formatPrice } from '../utils/currency';
import { OrderSummary } from '../components/checkout/OrderSummary';
import { Logo } from '../components/brand/Logo';
import { useSeo } from '../hooks/useSeo';
const payments: {
  id: PaymentMethod;
  label: string;
}[] = [{
  id: 'ideal',
  label: 'iDEAL'
}, {
  id: 'card',
  label: 'Creditcard'
}, {
  id: 'klarna',
  label: 'Klarna — achteraf betalen'
}, {
  id: 'paypal',
  label: 'PayPal'
}];
export function Checkout() {
  const {
    lines,
    subtotal,
    clear
  } = useCart();
  const form = useCheckoutForm();
  useSeo({
    title: 'Afrekenen | AMIRA Beauty',
    description: 'Veilig afrekenen bij AMIRA Beauty.',
    path: '/afrekenen',
    noindex: true
  });
  const [status, setStatus] = useState<'idle' | 'processing' | 'done'>('idle');
  const [order, setOrder] = useState<{
    number: string;
    total: number;
  } | null>(null);
  const shipping = shippingCost(subtotal, form.shipping);
  const total = subtotal + shipping;
  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.validate()) return;
    setStatus('processing');
    window.setTimeout(() => {
      setOrder({
        number: `AM-${Math.floor(100000 + Math.random() * 900000)}`,
        total
      });
      clear();
      setStatus('done');
    }, 900);
  };
  return <div className="min-h-screen w-full bg-canvas" data-id="element-419" data-mp="3qb1fbkkhgc5f">
      <header className="border-b border-line" data-id="element-420" data-mp="3uefz8mbjlco5">
        <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-5 lg:px-8" data-id="element-421" data-mp="3uf98btxz4e9g">
          <Link to="/" aria-label="AMIRA Beauty, naar home" data-id="element-422" data-mp="15lu973nmox1j"><Logo data-id="element-423" data-mp="19n5z7aaezka9" /></Link>
          <span className="flex items-center gap-2 text-sm text-muted" data-id="element-424" data-mp="19me400kqboki">
            <LockIcon className="h-4 w-4" data-id="element-425" data-mp="36ntpa1vdzw3l" /> Veilig afrekenen
          </span>
        </div>
      </header>

      {status === 'done' && order ? <motion.div initial={{
      opacity: 0,
      y: 8
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.25,
      ease: [0.23, 1, 0.32, 1]
    }} className="mx-auto flex max-w-lg flex-col items-center px-5 py-24 text-center" data-id="element-426" data-mp="31oyxwbjcsz7v">
          <span className="grid h-14 w-14 place-items-center rounded-full bg-olive text-canvas" data-id="element-427" data-mp="2n1dvkjuaz410">
            <CheckIcon className="h-6 w-6" data-id="element-428" data-mp="2izsbadffvngs" />
          </span>
          <h1 className="mt-6 font-display text-4xl uppercase tracking-display" data-id="element-429" data-mp="2wq5i2cglgw4j">Bedankt, {form.fields.firstName}!</h1>
          <p className="mt-3 text-lg text-muted" data-id="element-430" data-mp="2ryx9fin39esf">
            Bestelling <strong className="text-ink" data-id="element-431" data-mp="2rxcfnv8llzvm">{order.number}</strong> ({formatPrice(order.total)}) is ontvangen. Je ontvangt de
            bevestiging op {form.fields.email}.
          </p>
          <p className="mt-6 rounded-2xl bg-surface px-5 py-4 text-sm" data-id="element-432" data-mp="1za8drz4sy1o2">Verwachte levering: <strong data-id="element-433" data-mp="ero48huco2g5">morgen</strong></p>
          <Link to="/" className="mt-8 inline-flex h-12 items-center rounded-full bg-ink px-6 text-sm font-medium text-canvas hover:bg-berry" data-id="element-434" data-mp="eqmjtmrg23lr">
            Verder winkelen
          </Link>
        </motion.div> : lines.length === 0 ? <div className="mx-auto flex max-w-lg flex-col items-center px-5 py-32 text-center" data-id="element-435" data-mp="aqm1vs54za9y">
          <h1 className="font-display text-3xl uppercase tracking-display" data-id="element-436" data-mp="yg6r2srvv3ro">Je winkelwagen is leeg</h1>
          <p className="mt-2 text-muted" data-id="element-437" data-mp="12itkuz846yri">Voeg eerst iets toe om af te rekenen.</p>
          <Link to="/collectie/bestsellers" className="mt-8 inline-flex h-12 items-center rounded-full bg-ink px-6 text-sm font-medium text-canvas hover:bg-berry" data-id="element-438" data-mp="oisljo7hzze5">
            Shop bestsellers
          </Link>
        </div> : <form onSubmit={submit} noValidate className="mx-auto grid max-w-6xl gap-10 px-5 py-10 lg:grid-cols-12 lg:gap-16 lg:px-8 lg:py-14" data-id="element-439" data-mp="397q04d3nsza8">
          <div className="space-y-10 lg:col-span-7" data-id="element-440" data-mp="3e5rnvbc15ms1">
            <h1 className="font-display text-3xl uppercase tracking-display sm:text-4xl" data-id="element-441" data-mp="3tij4b4vx5bqc">Afrekenen</h1>

            <fieldset data-id="element-442" data-mp="3oppxmbgw3tbm">
              <legend className="text-lg font-semibold" data-id="element-443" data-mp="3ophlwmwi6u20">Contact</legend>
              <div className="mt-4" data-id="element-444" data-mp="3kq8tbld79281">
                <Field id="email" label="E-mailadres" type="email" autoComplete="email" form={form} data-id="element-445" data-mp="3kn35e9gb46dw" />
              </div>
            </fieldset>

            <fieldset data-id="element-446" data-mp="gb91410837ke">
              <legend className="text-lg font-semibold" data-id="element-447" data-mp="g9o84ftk3won">Bezorgadres</legend>
              <div className="mt-4 grid gap-4 sm:grid-cols-2" data-id="element-448" data-mp="c94916pfaizv">
                <Field id="firstName" label="Voornaam" autoComplete="given-name" form={form} data-id="element-449" data-mp="c7jgtnqkzc56" />
                <Field id="lastName" label="Achternaam" autoComplete="family-name" form={form} data-id="element-450" data-mp="7c42yvskpg8r" />
                <div className="sm:col-span-2" data-id="element-451" data-mp="2dkfgqybzwea">
                  <Field id="address" label="Straat en huisnummer" autoComplete="street-address" form={form} data-id="element-452" data-mp="2s1jp3dgukgs2" />
                </div>
                <Field id="postcode" label="Postcode" autoComplete="postal-code" form={form} data-id="element-453" data-mp="2n0457oqeogb9" />
                <Field id="city" label="Plaats" autoComplete="address-level2" form={form} data-id="element-454" data-mp="2czbk1xu3xrnr" />
              </div>
            </fieldset>

            <fieldset data-id="element-455" data-mp="2mvoe4wtke7j1">
              <legend className="text-lg font-semibold" data-id="element-456" data-mp="2mvekmz9b9i8l">Verzending</legend>
              <div className="mt-4 grid gap-2" data-id="element-457" data-mp="2rmehk4ifk0b3">
                {shippingOptions.map(o => {
              const price = shippingCost(subtotal, o.id);
              return <Option key={o.id} name="shipping" checked={form.shipping === o.id} onChange={() => form.setShipping(o.id)} data-id="element-458" data-mp="2vqcf4as3x321">
                      <span className="flex-1" data-id="element-459" data-mp="35lml19lmlul3">
                        <span className="block font-medium" data-id="element-460" data-mp="35mxkix81gg6e">{o.label}</span>
                        <span className="block text-sm text-muted" data-id="element-461" data-mp="3ajxtph06pz1q">{o.detail}</span>
                      </span>
                      <span className="text-sm font-medium" data-id="element-462" data-mp="1b9pox3c8o0jp">{price === 0 ? 'Gratis' : formatPrice(price)}</span>
                    </Option>;
            })}
                {subtotal < FREE_SHIPPING_THRESHOLD && <p className="text-sm text-muted" data-id="element-463" data-mp="137gnp0yfwtkz">Nog {formatPrice(FREE_SHIPPING_THRESHOLD - subtotal)} tot gratis standaard verzending.</p>}
              </div>
            </fieldset>

            <fieldset data-id="element-464" data-mp="1d5mnnd0orpn7">
              <legend className="text-lg font-semibold" data-id="element-465" data-mp="1d53eakp3q9ks">Betaalmethode</legend>
              <div className="mt-4 grid gap-2 sm:grid-cols-2" data-id="element-466" data-mp="1r5e89wlpk4a6">
                {payments.map(p => <Option key={p.id} name="payment" checked={form.payment === p.id} onChange={() => form.setPayment(p.id)} data-id="element-467" data-mp="1m6kt7zqrw57w">
                    <span className="font-medium" data-id="element-468" data-mp="20vh382ce3sb5">{p.label}</span>
                  </Option>)}
              </div>
            </fieldset>

            <div className="lg:hidden" data-id="element-469" data-mp="1vygu1ik8u9ft">
              <OrderSummary lines={lines} subtotal={subtotal} shipping={shipping} data-id="element-470" data-mp="29v448n17iw3o" />
            </div>

            <button type="submit" disabled={status === 'processing'} className="flex h-14 w-full items-center justify-center gap-2 rounded-full bg-ink text-base font-medium text-canvas transition-colors duration-150 hover:bg-berry disabled:opacity-70" data-id="element-471" data-mp="25v3ew7ckl0hu">
              {status === 'processing' ? <>
                  <LoaderIcon className="h-4 w-4 animate-spin" data-id="element-472" data-mp="2kgqy9qay80zn" /> Bezig met betalen…
                </> : <>Betaal {formatPrice(total)}</>}
            </button>
            <p className="-mt-6 text-center text-xs text-muted" data-id="element-473" data-mp="2ajcuora4j6or">30 dagen bedenktijd · Veilig betalen via SSL</p>
          </div>

          <div className="hidden lg:col-span-5 lg:block" data-id="element-474" data-mp="1wsg7jfyyvhp3">
            <div className="sticky top-8" data-id="element-475" data-mp="20t034f3w6d94">
              <OrderSummary lines={lines} subtotal={subtotal} shipping={shipping} data-id="element-476" data-mp="20ttclnu8jguy" />
            </div>
          </div>
        </form>}
    </div>;
}
type FieldProps = {
  id: keyof CheckoutFields;
  label: string;
  type?: string;
  autoComplete?: string;
  form: ReturnType<typeof useCheckoutForm>;
};
function Field({
  id,
  label,
  type = 'text',
  autoComplete,
  form
}: FieldProps) {
  const error = form.errors[id];
  return <div data-id="element-477" data-mp="22c1qn5y6k2ay">
      <label htmlFor={`co-${id}`} className="text-sm font-medium" data-id="element-478" data-mp="229fgqznthapk">{label}</label>
      <input id={`co-${id}`} type={type} autoComplete={autoComplete} value={form.fields[id]} onChange={e => form.update(id, e.target.value)} aria-invalid={Boolean(error)} aria-describedby={error ? `co-${id}-err` : undefined} className={`mt-1.5 h-12 w-full rounded-xl border bg-white px-4 text-ink focus:outline-none ${error ? 'border-pink focus:border-pink' : 'border-line focus:border-ink'}`} data-id="element-479" data-mp="1y7txmwkow06x" />
      {error && <p id={`co-${id}-err`} className="mt-1.5 text-sm text-berry" data-id="element-480" data-mp="1alx87ctzdpcd">{error}</p>}
    </div>;
}
function Option({
  name,
  checked,
  onChange,
  children
}: {
  name: string;
  checked: boolean;
  onChange: () => void;
  children: React.ReactNode;
}) {
  return <label className={`flex cursor-pointer items-center gap-4 rounded-xl border bg-white px-4 py-4 transition-colors duration-150 ${checked ? 'border-ink ring-1 ring-ink' : 'border-line hover:border-muted'}`} data-id="element-481" data-mp="3c9rnj55ott55">
      <input type="radio" name={name} checked={checked} onChange={onChange} className="h-4 w-4 accent-[#1C1714]" data-id="element-482" data-mp="3m7xnvibuir7w" />
      {children}
    </label>;
}